"use client";

import React, { memo } from "react";
import { cn } from "@/lib/utils";
import { VybeAgentMark } from "./VybeAgentMark";

/** Assistant tool part for the gated `place_order` tool (matches AI SDK UI shape). */
export interface PlaceOrderToolPart {
  type: "tool-place_order";
  toolCallId: string;
  state:
    | "input-streaming"
    | "input-available"
    | "output-available"
    | "output-error";
  input?: unknown;
  output?: unknown;
  errorText?: string;
}

interface OrderInput {
  action?: string;
  symbol?: string;
  volume?: number;
  stopLoss?: number;
  takeProfit?: number;
}

interface OrderOutput {
  status?: string;
  action?: string;
  symbol?: string;
  volume?: number;
  price?: number;
}

function summarize(part: PlaceOrderToolPart): string {
  const input = (part.input ?? {}) as OrderInput;
  const action = input.action ?? "";
  const symbol = input.symbol ?? "";
  const volume = input.volume != null ? `${input.volume} ` : "";
  const base = `${action} ${volume}${symbol}`.trim();
  return base || "place_order";
}

function statusText(part: PlaceOrderToolPart): {
  label: string;
  tone: "pending" | "sent" | "error";
} {
  switch (part.state) {
    case "input-streaming":
    case "input-available":
      return { label: "Awaiting consent", tone: "pending" };
    case "output-available": {
      const out = (part.output ?? {}) as OrderOutput;
      if (out.status === "sent") {
        const px = out.price != null ? ` @ ${out.price}` : "";
        return { label: `Sent${px}`, tone: "sent" };
      }
      return { label: "Done", tone: "sent" };
    }
    case "output-error":
      return { label: part.errorText || "Rejected", tone: "error" };
    default:
      return { label: part.state, tone: "pending" };
  }
}

function PlaceOrderToolBubbleImpl({ part }: { part: PlaceOrderToolPart }) {
  const summary = summarize(part);
  const { label, tone } = statusText(part);

  const badgeClass =
    tone === "error"
      ? "bg-destructive/15 text-destructive border-destructive/30"
      : tone === "sent"
        ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/25"
        : "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/25";

  return (
    <div className="flex min-w-0 gap-2.5">
      <div className="mt-0.5 shrink-0">
        <VybeAgentMark size={14} tone="primary" />
      </div>
      <div className="flex-1 min-w-0 rounded-md border border-border bg-muted/20 overflow-hidden">
        <div className="flex w-full min-w-0 items-center gap-2 overflow-hidden px-2 py-1.5">
          <span className="min-w-0 flex-1 truncate text-[10px] font-mono text-foreground">
            {`place_order · ${summary}`}
          </span>
          <span
            className={cn(
              "text-[9px] uppercase tracking-wide shrink-0 rounded border px-1 py-px max-w-[55%] truncate",
              badgeClass,
            )}
            title={label}
          >
            {label}
          </span>
        </div>
      </div>
    </div>
  );
}

export const PlaceOrderToolBubble = memo(PlaceOrderToolBubbleImpl);
PlaceOrderToolBubble.displayName = "PlaceOrderToolBubble";
